import { useState } from "react";
import { CalendarClock, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlatformBadge, REGUL_LABELS } from "@/components/Badges";
import { api, errMsg, money } from "@/lib/api";
import { ReclamationForm } from "@/components/ReclamationForm";

const today = () => new Date().toISOString().slice(0, 10);

const SuiviForm = ({ rid, onAdded }) => {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post(`/reservations/${rid}/suivis`, { date, note }); setNote(""); toast.success("Suivi ajouté"); onAdded(); }
    catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border border-slate-200 p-3" data-testid="suivi-form">
      <div className="flex gap-2">
        <DateField required value={date} onChange={setDate} className="w-40" data-testid="suivi-date-input" />
        <Button type="submit" size="sm" disabled={!note.trim() || loading} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="suivi-add-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}Ajouter
        </Button>
      </div>
      <Textarea rows={2} required placeholder="Ex. : Lettre envoyée au support Airbnb, dossier n° 12345" value={note} onChange={(e) => setNote(e.target.value)} data-testid="suivi-note-input" />
    </form>
  );
};

const RelanceField = ({ r, onSaved }) => {
  const [val, setVal] = useState(r.regularisation.relance_date || "");
  const save = async () => {
    try { await api.put(`/reservations/${r.id}/regularisation`, { statut: r.regularisation.statut, relance_date: val }); toast.success(val ? "Relance planifiée" : "Relance retirée"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-end gap-2">
      <div className="space-y-1"><Label className="text-xs">Prochaine relance</Label>
        <DateField value={val} onChange={setVal} className="w-40" data-testid="relance-date-input" /></div>
      <Button size="sm" variant="outline" className="h-9" onClick={save} data-testid="relance-save-button"><CalendarClock className="h-4 w-4 mr-1" />Enregistrer</Button>
    </div>
  );
};

export const SuiviDialog = ({ r, onClose, onChanged }) => {
  const setStatut = async (statut) => {
    try { await api.put(`/reservations/${r.id}/regularisation`, { statut }); toast.success(`Statut : ${REGUL_LABELS[statut]}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (sid) => {
    try { await api.delete(`/reservations/${r.id}/suivis/${sid}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const suivis = [...(r?.regularisation?.suivis || [])].sort((x, y) => (y.date + y.created_at).localeCompare(x.date + x.created_at));

  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      {r && (
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="suivi-dialog">
          <DialogHeader>
            <DialogTitle>Suivi de régularisation · <span className="font-mono">{r.code}</span></DialogTitle>
            <DialogDescription className="flex items-center gap-2"><PlatformBadge plateforme={r.plateforme} /> Écart à régulariser : <b className="tabular text-red-600">{money(r.ecart)}</b></DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Statut</Label>
              <Select value={r.regularisation.statut} onValueChange={setStatut}>
                <SelectTrigger className="h-9 w-36" data-testid="suivi-statut-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(REGUL_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`suivi-statut-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <RelanceField key={r.id + (r.regularisation.relance_date || "")} r={r} onSaved={onChanged} />
          </div>
          <ReclamationForm key={r.id + JSON.stringify(r.regularisation.reclamation || {})} r={r} onSaved={onChanged} />
          <SuiviForm rid={r.id} onAdded={onChanged} />
          <div className="max-h-60 overflow-y-auto" data-testid="suivi-history">
            {suivis.length === 0 ? <p className="text-sm text-slate-500">Aucun suivi pour l'instant.</p> : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-3">
                {suivis.map((s) => (
                  <li key={s.id} className="ml-4" data-testid={`suivi-item-${s.id}`}>
                    <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-600" />
                    <div className="flex items-start justify-between gap-2">
                      <div><p className="text-xs font-mono text-slate-500">{s.date}</p><p className="text-sm whitespace-pre-wrap">{s.note}</p></div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => remove(s.id)} data-testid={`suivi-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};
